import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { FormTemplate, FormField, FormSection } from '../../types/formBuilder';
import { TemplateService } from '../../services/templateService';
import LoadingScreen from '../common/LoadingScreen';
import { getSectionColorHex } from '../../utils/sectionColors';
import '../../styles/toggle-switch.css';
import '../../styles/signature-canvas.css';
import SignatureCanvas from 'react-signature-canvas';
import { 
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

interface FormRendererProps {
  templateId?: string;
  template?: FormTemplate;
  onSubmit: (data: any) => Promise<void>;
  onCancel?: () => void;
  initialData?: any;
  readOnly?: boolean;
  className?: string;
  isSubmitting?: boolean;
  showCancelButton?: boolean;
}

const FormRenderer: React.FC<FormRendererProps> = ({
  templateId,
  template: providedTemplate,
  onSubmit,
  onCancel,
  initialData = {},
  readOnly = false,
  className = '',
  isSubmitting = false,
  showCancelButton = true
}) => {
  const { t } = useLanguage();
  const [template, setTemplate] = useState<FormTemplate | null>(providedTemplate || null);
  const [loading, setLoading] = useState(!providedTemplate && !!templateId);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: initialData
  });

  // Signature refs and states for all signature fields
  const signatureRefs = useRef<{ [key: string]: SignatureCanvas | null }>({});
  const [signatureData, setSignatureData] = useState<{ [key: string]: string }>({});

  // Load template if templateId is provided
  useEffect(() => {
    if (templateId && !providedTemplate) {
      loadTemplate();
    }
  }, [templateId, providedTemplate]);

  const loadTemplate = async () => {
    if (!templateId) return;
    
    setLoading(true);
    try {
      const loadedTemplate = await TemplateService.getFormTemplate(templateId);
      if (loadedTemplate) {
        setTemplate(loadedTemplate);
      } else {
        toast.error(t('formRenderer.templateNotFound') || 'Form template not found');
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error(t('formRenderer.loadError') || 'Error loading form template');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    if (readOnly) return;
    
    setSubmitting(true);
    try {
      await onSubmit(data);
      toast.success(t('formRenderer.submitSuccess') || 'Form submitted successfully');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('formRenderer.submitError') || 'Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    // Use field.id as the primary field name for form registration
    // This ensures we have a clean, valid identifier
    let fieldName = field.id;
    
    // Fallback if no id exists (shouldn't happen, but be safe)
    if (!fieldName || (typeof fieldName === 'string' && fieldName.trim() === '')) {
      fieldName = `${field.type}_${Date.now()}`;
      console.warn('Generated fieldName for field without id:', fieldName, field);
    }
    
    console.log('Rendering field:', { 
      id: field.id, 
      name: field.name, 
      fieldName, 
      type: field.type 
    });
    
    // Checkbox/Switch validáció - value === true ellenőrzés
    const isCheckboxOrSwitch = field.type === 'checkbox' || field.type === 'switch';
    const checkboxErrorMessage = field.validations?.find(v => v.rule === 'required')?.message || 
                                 (field.type === 'switch' 
                                   ? (t('formBuilder.validation.switchRequired') || 'This switch must be enabled')
                                   : (t('formBuilder.validation.checkboxRequired') || 'This checkbox must be checked'));
    
    const fieldProps = {
      ...register(fieldName, {
        required: field.required ? (field.validations?.find(v => v.rule === 'required')?.message || 'This field is required') : false,
        validate: isCheckboxOrSwitch && field.required 
          ? (value) => value === true || checkboxErrorMessage
          : undefined,
        minLength: field.validations?.find(v => v.rule === 'min_length')?.value ? {
          value: parseInt(String(field.validations.find(v => v.rule === 'min_length')!.value)),
          message: field.validations.find(v => v.rule === 'min_length')?.message || `Minimum ${field.validations.find(v => v.rule === 'min_length')?.value} characters`
        } : undefined,
        maxLength: field.validations?.find(v => v.rule === 'max_length')?.value ? {
          value: parseInt(String(field.validations.find(v => v.rule === 'max_length')!.value)),
          message: field.validations.find(v => v.rule === 'max_length')?.message || `Maximum ${field.validations.find(v => v.rule === 'max_length')?.value} characters`
        } : undefined,
        pattern: field.validations?.find(v => v.rule === 'pattern')?.value ? {
          value: new RegExp(String(field.validations.find(v => v.rule === 'pattern')!.value)),
          message: field.validations.find(v => v.rule === 'pattern')?.message || 'Invalid format'
        } : undefined
      }),
      disabled: readOnly,
      className: "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
    };
    
    const fieldError = errors[fieldName];

    const widthClass = field.width === 'half' ? 'md:w-1/2' : 
                     field.width === 'third' ? 'md:w-1/3' : 
                     field.width === 'quarter' ? 'md:w-1/4' : 'w-full';

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
              placeholder={field.placeholder}
              {...fieldProps}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              rows={4}
              placeholder={field.placeholder}
              {...fieldProps}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select {...fieldProps}>
              <option value="">{field.placeholder || 'Select an option...'}</option>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...fieldProps}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option) => (
                <div key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    value={option.value}
                    {...register(fieldName, { required: field.required })}
                    disabled={readOnly}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              {...fieldProps}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              placeholder={field.placeholder}
              {...fieldProps}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'address':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              rows={3}
              placeholder={field.placeholder || 'Enter full address...'}
              {...fieldProps}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'currency':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              step="0.01"
              placeholder={field.placeholder || '0.00'}
              {...fieldProps}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'time':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="time"
              {...fieldProps}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option) => (
                <div key={option.value} className="flex items-center">
                  <input
                    type="checkbox"
                    value={option.value}
                    {...register(fieldName, { required: field.required })}
                    disabled={readOnly}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'switch':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <label className="switch">
                <input
                  type="checkbox"
                  {...fieldProps}
                />
                <span className="slider"></span>
              </label>
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'file':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="file"
              multiple={field.config?.multiple || false}
              accept={field.config?.accept || '*/*'}
              {...fieldProps}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            <div className="mt-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>
                {field.config?.multiple ? 'Multiple files allowed' : 'Single file only'}
                {field.config?.maxSize && ` • Max ${Math.round(field.config.maxSize / (1024 * 1024))}MB`}
              </span>
              {field.config?.accept && field.config.accept !== '*/*' && (
                <span className="text-primary-600 dark:text-primary-400">
                  {field.config.accept}
                </span>
              )}
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'color':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="color"
              {...fieldProps}
              className="h-10 w-20 rounded border border-gray-300 dark:border-gray-600"
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'range':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="range"
              min={field.config?.min || 0}
              max={field.config?.max || 100}
              step={field.config?.step || 1}
              {...fieldProps}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'rating':
        const maxRating = field.config?.max || 5;
        const currentRating = watch(fieldName) || 0;
        
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex items-center space-x-1">
              {[...Array(maxRating)].map((_, index) => {
                const ratingValue = index + 1;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setValue(fieldName, ratingValue)}
                    disabled={readOnly}
                    className="focus:outline-none disabled:cursor-not-allowed"
                  >
                    {ratingValue <= currentRating ? (
                      <StarSolidIcon className="h-6 w-6 text-yellow-400" />
                    ) : (
                      <StarIcon className="h-6 w-6 text-gray-300 hover:text-yellow-400 transition-colors" />
                    )}
                  </button>
                );
              })}
              {currentRating > 0 && (
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  {currentRating}/{maxRating}
                </span>
              )}
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'signature':
        const clearSignature = () => {
          const ref = signatureRefs.current[fieldName];
          if (ref) {
            ref.clear();
            setSignatureData(prev => ({ ...prev, [fieldName]: '' }));
            setValue(fieldName, '');
          }
        };

        const saveSignature = () => {
          const ref = signatureRefs.current[fieldName];
          if (ref) {
            const dataURL = ref.toDataURL();
            setSignatureData(prev => ({ ...prev, [fieldName]: dataURL }));
            setValue(fieldName, dataURL);
          }
        };

        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className={`border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${readOnly ? 'pointer-events-none opacity-60' : ''}`}>
              <SignatureCanvas
                ref={(ref) => { signatureRefs.current[fieldName] = ref; }}
                canvasProps={{
                  width: 400,
                  height: 200,
                  className: `signature-canvas w-full h-48 rounded-lg ${readOnly ? 'cursor-not-allowed' : 'cursor-crosshair'}`
                }}
                backgroundColor="rgba(255,255,255,0)"
                penColor="#000000"
                onEnd={saveSignature}
              />
              {!readOnly && (
                <div className="flex justify-between items-center p-3 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Sign above with your mouse or touch device
                  </p>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="flex items-center space-x-1 px-3 py-1 text-xs text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span>Clear</span>
                  </button>
                </div>
              )}
            </div>
            <input
              type="hidden"
              {...fieldProps}
              value={signatureData[fieldName] || ''}
            />
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      case 'image':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md dark:border-gray-600">
              <div className="space-y-1 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600 dark:text-gray-400">
                  <label className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                    <span>Upload {field.config?.multiple ? 'images' : 'an image'}</span>
                    <input
                      type="file"
                      multiple={field.config?.multiple || false}
                      accept={field.config?.accept || 'image/*'}
                      {...fieldProps}
                      className="sr-only"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {field.config?.accept || 'PNG, JPG, GIF'} 
                  {field.config?.maxSize ? ` up to ${Math.round(field.config.maxSize / (1024 * 1024))}MB` : ' up to 10MB'}
                  {field.config?.multiple && ' (multiple files)'}
                </p>
              </div>
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
            {fieldError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldError?.message as string}
              </p>
            )}
          </div>
        );

      // Unsupported field types - show placeholder
      case 'customer_lookup':
      case 'product_lookup':
      case 'user_lookup':
      case 'organization_lookup':
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {field.type.charAt(0).toUpperCase() + field.type.slice(1).replace('_', ' ')} field
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                This field type is not yet supported in form preview
              </p>
            </div>
            {field.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{field.description}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={field.id} className={`${widthClass} px-2 mb-4`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="p-4 border-2 border-dashed border-red-300 dark:border-red-600 rounded-lg text-center">
              <p className="text-sm text-red-600 dark:text-red-400">
                Unknown field type: {field.type}
              </p>
            </div>
          </div>
        );
    }
  };

  const renderSection = (section: FormSection) => {
    const sectionFields = template?.fields.filter(field => field.section === section.id) || [];
    const sectionColor = getSectionColorHex(section.color || 'primary');
    
    return (
      <div key={section.id} className="mb-8">
        <div 
          className="p-4 rounded-lg border-l-4 mb-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700" 
          style={{ borderLeftColor: sectionColor }}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {section.label || section.name}
          </h3>
          {section.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {section.description}
            </p>
          )}
        </div>
        
        <div className="flex flex-wrap -mx-2">
          {sectionFields.map(renderField)}
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (!template) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">
          {t('formRenderer.noTemplate') || 'No form template available'}
        </p>
      </div>
    );
  }

  const sections = template.sections.sort((a, b) => (a.order || 0) - (b.order || 0));
  const unassignedFields = template.fields.filter(field => !field.section);

  return (
    <div className={`max-w-4xl mx-auto ${className}`}>
      {/* Form Header */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          {template.settings.title || template.name}
        </h1>
        {template.settings.description && (
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {template.settings.description}
          </p>
        )}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(handleFormSubmit)} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-8">
          {/* Progress Bar */}
          {template.settings.multiStep && template.settings.showProgressBar && sections.length > 1 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Step {currentStep + 1} of {sections.length}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {Math.round(((currentStep + 1) / sections.length) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / sections.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Sections */}
          {template.settings.multiStep ? (
            // Multi-step: Show only current section
            sections[currentStep] && renderSection(sections[currentStep])
          ) : (
            // Single page: Show all sections
            <>
              {sections.map(renderSection)}
              
              {/* Unassigned Fields */}
              {unassignedFields.length > 0 && (
                <div className="mb-8">
                  <div className="flex flex-wrap -mx-2">
                    {unassignedFields.map(renderField)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Form Actions */}
        <div className="px-8 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="flex space-x-3">
              {onCancel && showCancelButton && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {template.settings.cancelButtonText || t('common.cancel') || 'Cancel'}
                </button>
              )}
              
              {template.settings.multiStep && currentStep > 0 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Previous
                </button>
              )}
            </div>

            <div className="flex space-x-3">
              {template.settings.multiStep && currentStep < sections.length - 1 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Next
                  <ArrowRightIcon className="h-4 w-4 ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting || isSubmitting || readOnly}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(submitting || isSubmitting) ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4 mr-2" />
                      {template.settings.submitButtonText || t('common.submit') || 'Submit'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FormRenderer;
